/** Owns the header + drives the carousel via a handle. Mirrors TestimonialsSection. */
import { useRef } from 'react'
import { Container } from '@educator/components/ProgramSections/Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '@educator/components/ProgramSections/i18n'
import { CarouselHeader } from '@educator/components/ProgramSections/shared/CarouselHeader'
import { useRowScroll } from '@educator/components/ProgramSections/shared/useRowScroll'
import { useTranslation } from '@mc/i18n'
import {
  ValuePromiseCarousel,
  type ValuePromiseCarouselHandle,
} from './ValuePromiseCarousel'
import { VALUE_PROMISE_MEDIA } from './constants/valuePromiseCards'
import type { ValuePromiseItem } from './types'

interface ValuePromiseCopy {
  id: string
  headline: string
  body: string
}

export const ValuePromiseSection = () => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)
  const carouselRef = useRef<ValuePromiseCarouselHandle>(null)
  const { scrollRow } = useRowScroll(carouselRef)

  // Merge i18n copy with per-card media by id; drop cards with no media entry.
  const rawCopy = t('valuePromise.items', { returnObjects: true })
  const copy = Array.isArray(rawCopy) ? (rawCopy as ValuePromiseCopy[]) : []
  const items: ValuePromiseItem[] = copy.flatMap((c) => {
    const media = VALUE_PROMISE_MEDIA[c.id]
    return media ? [{ ...c, ...media }] : []
  })

  return (
    <section
      id='value-promise'
      data-section='value-promise'
      className='tailwind-enabled bg-edu-bg-primary py-6 text-edu-fg-primary md:py-12'
    >
      <Container bleed>
        <div className='flex flex-col gap-6 md:gap-4'>
          <CarouselHeader
            title={
              <>
                <span className='md:hidden'>
                  {t('valuePromise.headlineMobile')}
                </span>
                <span className='hidden md:inline'>
                  {t('valuePromise.headline')}
                </span>
              </>
            }
            prevLabel={t('valuePromise.previousLabel')}
            nextLabel={t('valuePromise.nextLabel')}
            onNav={scrollRow}
            className='px-6 md:px-25'
          />

          <ValuePromiseCarousel
            ref={carouselRef}
            items={items}
            sectionLabel={t('valuePromise.sectionLabel')}
            viewMoreLabel={t('valuePromise.viewMore')}
            closeLabel={t('valuePromise.close')}
          />
        </div>
      </Container>
    </section>
  )
}
